import React from "react";
import { Helmet } from "react-helmet";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Stack,
  Link,
  useTheme
} from "@mui/material";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import PageTransition from "../components/PageTransition";
import ScrollReveal from "../components/ScrollReveal";
import { captureContactClick } from "../analytics";
import { MEASURE } from "../data/studioStory";

function AboutPage() {
  const theme = useTheme();

  return (
    <PageTransition>
      <Helmet>
        <title>About – David Kooi</title>
        <link rel="canonical" href="https://davidkooi.com/about/" />
        <meta name="description" content="Learn about David Kooi – founder of Skylark Creations, an AI-native product studio, and independent AI-orchestration consultant running client engagements end to end. Former CEO of Jointly." />
      </Helmet>
      
      <Box sx={{ minHeight: "100vh", backgroundColor: theme.palette.background.default }}>
        <Navigation />
      
      <Container component="main" id="main" maxWidth="md" sx={{ py: 6 }}>
        {/* Prose measure. The md container is 852px, which runs ~107 characters
            per line here — well past the ~80 the canon asks for and past where
            the eye reliably finds the next line. */}
        <Stack spacing={4} sx={{ maxWidth: MEASURE, mx: "auto", width: "100%" }}>
          <ScrollReveal>
            <Typography variant="h3" component="h1" sx={{ fontWeight: 800, textAlign: "center" }}>
              About David Kooi
            </Typography>
          </ScrollReveal>

          <ScrollReveal delay={0.1}>
            <Box>
              <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                Now
              </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              I founded <Link href="https://skylarkcreations.com" target="_blank" rel="noopener" color="primary" underline="hover">Skylark Creations</Link>, a one-person, AI-native product studio in Colorado. I design, build, and ship apps and tools from zero to live, using AI at every layer of the stack &ndash; reasoning partners for architecture, coding agents for implementation, and human advisors for taste and truth.
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              The studio runs as a real portfolio, operated day to day by a fleet of AI agents I orchestrate. A manager AI runs each product&rsquo;s daily cycle. Every report gets cold-audited against its own claims. Only the decisions that genuinely need a human reach me.
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              In 2026 that same operating system started running client engagements end to end, with me working as an independent AI-orchestration consultant. Engagements are async-first &ndash; artifacts land in your inbox instead of meetings on your calendar &ndash; and a reaction sent one evening routinely comes back as a verified, deployed change the next day.
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              A few habits define the practice.
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              Nothing a client says evaporates. Every round of feedback is broken into numbered points, and each point gets a written disposition &ndash; accepted, deferred, or noted, with the reason &ndash; in a ledger that runs for the life of the engagement.
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              My own improvements have to pass pre-registered gates. The pass/fail rule and the evaluation data are frozen before any results exist, and work that fails its gate is discarded rather than shipped.
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              And before anything leaves, a second AI model family is prompted to refute it. Adversarial review as a release gate.
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              The products that are live are on the <Link component={RouterLink} to="/" color="primary" underline="hover">home page</Link>, and the whole portfolio lives at <Link href="https://skylarkcreations.com" target="_blank" rel="noopener" color="primary" underline="hover">Skylark Creations</Link>.
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              In development: <Link href="https://syntopica.ai/" target="_blank" rel="noopener" color="primary" underline="hover">Syntopica</Link>, a tool for cross-book synthesis using your Amazon library and Kindle highlights; <Link href="https://frolic.town/" target="_blank" rel="noopener" color="primary" underline="hover">Frolic</Link>, AI-powered outdoor activity recommendations; Dreamflyer, a meditative flying game; Urban Beaver, a relaxing dam-building puzzle game; and Billionaire Army, a civic-accountability platform that tracks U.S. billionaires against public-defined goals, with receipts.
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              I write about AI, philosophy, and product strategy at <Link href="https://uncagedminds.substack.com" target="_blank" rel="noopener" color="primary" underline="hover">Uncaged Minds</Link>.
            </Typography>
            </Box>
          </ScrollReveal>

          <ScrollReveal delay={0.2}>
            <Box>
              <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                Previously
              </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              I co-founded Jointly (2018&ndash;2025) and served as its CEO and Chief Product Officer &ndash; a purpose-driven cannabis wellness technology platform. I conceived and launched the industry&rsquo;s first AI-powered virtual assistant for personalized product recommendations, built on 500K+ user data points. Jointly grew to 500,000+ downloads with tens of thousands of monthly active users and 35% retention at 30 days, and I wrote the &ldquo;Theory of Purposeful Cannabis Consumption,&rdquo; which reframed cannabis as a tool for purposeful wellness.
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              We dissolved the company in 2025. We built for precision in a market that wanted speed, and we arrived early to an industry that wasn&rsquo;t ready to buy data. The dataset and the theory it produced outlived it &ndash; I&rsquo;m writing the book now.
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              From 2010 to 2021 I founded and ran Santa Monica Mountains Cyclery, building it into one of Southern California&rsquo;s top cycling retailers: ten consecutive years of revenue and profitability growth, a 4.92 average rating across 1,200+ five-star reviews, and drop-ship partnerships that grew online sales 60% year over year. I started Jointly in 2018 while still running the shop, and sold it to Trek Bicycle in 2021.
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              Earlier I held leadership roles at McMaster-Carr (2002&ndash;2009), where I oversaw a $1B+ distribution operation with 300+ staff, implemented e-commerce parametric search, and achieved 99.4% fulfillment accuracy. Before that, Click Commerce, Navistar, and other enterprises &ndash; mostly data-heavy operations and e-commerce.
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              I hold an MBA from the University of Chicago Booth School of Business (2002), a B.A. in Economics &amp; Statistics from Northwestern (1996), and a B.S. in Computer Science from Oregon State (2016) &ndash; the last one earned at 43, because I wanted to build things myself.
            </Typography>
            </Box>
          </ScrollReveal>

          <ScrollReveal delay={0.3}>
            <Box>
              <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                Interests & Personal
              </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              I&rsquo;m a naturalist &ndash; daily mindfulness, mountain hikes, and a running obsession with geology, botany, ecology, and early human history. (Ask me about mantle convection.) Those interests shape the products: technology that deepens a person&rsquo;s connection to the natural world instead of replacing it.
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              Beyond technology: cycling, community advocacy, piano, and banjo. The music leans bluegrass, jamgrass, folk, and reggae. My dog comes along on every adventure &ndash; and the best product insights tend to show up on long walks, not at the desk.
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              The through-line is calm tech: tools that respect attention instead of farming it. I&rsquo;d rather build supportive scaffolding than addictive slot machines, and I think AI should augment people, not audition to replace them.
            </Typography>
            </Box>
          </ScrollReveal>

          <ScrollReveal delay={0.4}>
            <Box>
              <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                Say Hi
              </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              I&rsquo;m up for good conversations about AI, entrepreneurship, product strategy, or philosophy. If you&rsquo;re building something interesting &ndash; or just want to compare notes &ndash; reach out.
            </Typography>
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              Email <Link href="mailto:hello@skylarkcreations.com" onClick={() => captureContactClick("about")} color="primary" underline="hover">hello@skylarkcreations.com</Link> or find me on <Link href="https://www.linkedin.com/in/david-d-kooi/" target="_blank" rel="noopener" color="primary" underline="hover">LinkedIn</Link>. Or ask <Link component={RouterLink} to="/chat" color="primary" underline="hover">ChatDDK</Link> &ndash; it&rsquo;s trained on my writing and it will answer for me, more or less.
            </Typography>
            </Box>
          </ScrollReveal>
        </Stack>
      </Container>

      <Footer />
    </Box>
  </PageTransition>
  );
}

export default AboutPage;
